import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "../api";
import type { ID } from "../id";

/**
 * A Federation is the body a team belongs to. The rows are flat and two
 * flags tell the three shapes apart: `is_primary` is ČNSMS, the federation
 * this installation runs; `is_umbrella` is WMS, which holds clubs from
 * states that have no federation of their own here; neither flag means
 * another state's federation, run by its own Správce federace.
 */
export interface Federation {
  id: ID;
  name: string;
  /** „ČNSMS". Optional: a federation nobody has abbreviated renders long. */
  short_name: string;
  country_code: string;
  is_primary: boolean;
  is_umbrella: boolean;
  /** Teams filed under it: what stands between it and a deletion. */
  teams_count: number;
  /** People holding Správce federace for it; a delete revokes those. */
  admins_count: number;
}

interface FederationsResponse {
  federations: Federation[];
}

export function useFederations(options?: { enabled?: boolean }) {
  return useQuery<Federation[]>({
    queryKey: ["federations"],
    queryFn: async () => {
      const res = await apiGet<FederationsResponse>("/federations");
      return res.federations;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * What a list of teams calls a federation: the abbreviation when there is
 * one, the full name otherwise. One helper, so „ČNSMS" and „Český národní
 * svaz mažoretkového sportu" cannot appear in two columns of one screen.
 */
export function federationLabel(f: {
  name: string;
  short_name?: string;
}): string {
  return f.short_name?.trim() || f.name;
}

export interface FederationPayload {
  name: string;
  short_name: string;
  country_code: string;
}

export function useCreateFederation() {
  const qc = useQueryClient();
  return useMutation<Federation, ApiError, FederationPayload>({
    mutationFn: (payload) => apiPost<Federation>("/federations", payload),
    // The dialog binds the errors to its fields (apiErrorToFormErrors).
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["federations"] });
    },
  });
}

/**
 * Removes a national federation. The server refuses ČNSMS and the umbrella,
 * and refuses any federation that still holds teams (409, naming how
 * many), so nothing here has to be re-derived.
 */
export function useDeleteFederation() {
  const qc = useQueryClient();
  return useMutation<{ roles_revoked: number }, ApiError, ID>({
    mutationFn: (id) => apiDelete<{ roles_revoked: number }>(`/federations/${id}`),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["federations"] });
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useUpdateFederation(id: ID) {
  const qc = useQueryClient();
  return useMutation<Federation, ApiError, FederationPayload>({
    mutationFn: (payload) => apiPatch<Federation>(`/federations/${id}`, payload),
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["federations"] });
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}
